import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { getRole, logout, refreshCurrentUser } from '../actions/userActions'

// Material UI Icons
import Home from '@material-ui/icons/Home'
import Assignment from '@material-ui/icons/Assignment'
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew'
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd'
import GroupIcon from '@material-ui/icons/Group'
import MergeTypeIcon from '@material-ui/icons/MergeType'
import Assessment from '@material-ui/icons/Assessment'
import Settings from '@material-ui/icons/Settings'
import PersonPinCircle from '@material-ui/icons/PersonPinCircle'
import ExpandMore from '@material-ui/icons/ExpandMore'
import ExpandLess from '@material-ui/icons/ExpandLess'
import DashboardIcon from '@material-ui/icons/Dashboard'
import TransferWithinAStationIcon from '@material-ui/icons/TransferWithinAStation'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WorkIcon from '@material-ui/icons/Work'
import AccountTreeIcon from '@material-ui/icons/AccountTree'
import ListIcon from '@material-ui/icons/List'
import TodayIcon from '@material-ui/icons/Today'
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn'
import MenuIcon from '@material-ui/icons/Menu'
import CloseIcon from '@material-ui/icons/Close'
import PaymentIcon from '@material-ui/icons/Payment'
import HistoryIcon from '@material-ui/icons/History'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'

import pjson from '../../package.json'

const Sidebar = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  // States
  const [name, setName] = useState('')
  const [role, setRole] = useState({})
  const [expandedGroup, setExpandedGroup] = useState('')
  const [expandedSubGroup, setExpandedSubGroup] = useState('')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Redux selectors
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { loadingGetRole, errorGetRole, getRoleData } = getRoleInfo
  const activeRole = getRoleData || userInfo?.role || role
  const isMerchantUser = activeRole?.name?.toLowerCase() === 'merchant' || !!userInfo?.merchantId
  const merchantQrPath = userInfo?.merchantQrToken ? `/admin/merchants/${userInfo.merchantQrToken}` : ''

  // ===== MENU GROUPS =====
  const adminMenuGroups = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      items: [
        { to: '/admin/caller-dashboard', label: 'Caller Dashboard', visible: !!role?.callerListView },
        { to: '/dashboard', label: 'Main Dashboard', visible: role.settings },
      ]
    },
    {
      id: 'management',
      title: 'User Management',
      items: [
        { to: '/users', label: 'Active Users', visible: role.usersView },
        // { to: '/customers', label: 'Customers', visible: role.customersView }
      ]
    },
    {
      id: 'administration',
      title: 'Administration',
      items: [
        { to: '/roles', label: 'Roles', visible: role.rolesView },
        { to: '/admin/audit-logs', label: 'Audit Log', visible: role.auditLogView },
        { to: '/admin/loan-types', label: 'Loan Types', visible: role.settings },
        { to: '/admin/app-build', label: 'Mobile App Build', visible: role.settings },
      ]
    },
    {
      id: 'loans',
      title: 'Loan Enquiries',
      items: [
        // Show enquiries list only if role allows viewing loan enquiries
        { to: '/admin/enquiries', label: 'Loan Enquiries', visible: !!role?.loanEnquiriesView },
        { to: '/admin/product-master', label: 'Product Master', visible: !!role?.productMasterView },
        { to: '/admin/payments', label: 'Payments', visible: !!role?.paymentView },
        { to: '/admin/enquiries/send-back', label: 'Send Back Enquiries', visible: !!role?.sendBackEnquiriesView },
        { to: '/admin/caller-list', label: 'Caller List', visible: !!role?.callerListView },
        { to: '/admin/prospects', label: 'Prospects', visible: !!role?.prospectsView },
        { to: '/admin/daily-call-report', label: 'Daily Call Form', visible: !!role?.dailyCallReportView },
        { to: '/admin/merchants/create', label: 'Onboard Merchant', visible: !!role?.merchantPageView },
        // Show QR Pool only if role has qrPoolView
        { to: '/admin/qrs', label: 'QR Pool', visible: !!role?.qrPoolView },
      ]
    },
    {
      id: 'agentProgram',
      title: 'Agent Program',
      items: [
        { to: '/admin/agent-program', label: 'Overview', visible: !!role?.agentProgramViewAll },
        { to: '/admin/agent-program/leads', label: 'Agent Program Leads', visible: !!(role?.agentProgramView || role?.agentProgramViewAll) },
        { to: '/admin/agent-program/config', label: 'Agent Program Config', visible: !!role?.agentProgramConfigView },
        { to: '/admin/agent-program/kyc', label: 'Agent KYC Verification', visible: !!role?.agentProgramKycView },
        { to: '/admin/agent-program/payout-requests', label: 'Payout Requests', visible: !!role?.agentProgramPayoutRelease },
        { to: '/admin/agent-program/wallet', label: 'Agent Wallet', visible: !!role?.agentProgramViewAll },
      ]
    },
  ]

  const merchantMenuGroups = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      items: [
        { to: '/merchant/dashboard', label: 'My Dashboard', visible: !!userInfo?.merchantId },
        { to: '/admin/caller-dashboard', label: 'Caller Dashboard', visible: !!role?.callerListView },
      ]
    },
    {
      id: 'caller',
      title: 'Loan Enquiries',
      items: [
        { to: merchantQrPath, label: 'My QR', visible: !!(role?.qrView && merchantQrPath) },
        { to: '/merchant/enquiries', label: 'My Enquiries', visible: !!(userInfo?.merchantId && role?.loanEnquiriesView) },
        { to: '/merchant/agent-program/dashboard', label: 'Agent Program', visible: !!userInfo?.merchantId },
        { to: '/merchant/agent-program/leads', label: 'Agent Program Leads', visible: !!(userInfo?.merchantId && role?.agentProgramView) },
      ]
    },
  ]

  const menuGroups = isMerchantUser ? merchantMenuGroups : adminMenuGroups

  // ===== ICONS =====
  const iconMapping = {
    "Main Dashboard": DashboardIcon,
    "My Dashboard": DashboardIcon,
    "Merchant Dashboard": DashboardIcon,
    "Task Count": Assessment,
    "Process List": ListIcon,
    "Daily Collection": TodayIcon,
    "Assign Process": AssignmentTurnedInIcon,
    "All Tasks": Assignment,
    "Task Allocation": AccountTreeIcon,
    "Bank Tasks": Assignment,
    "Field User Tasks": WorkIcon,
    "Unassigned Tasks": Assignment,
    "In Progress": WorkIcon,
    "Transferred": TransferWithinAStationIcon,
    "Finalisation Pending": Assignment,
    "Completed Tasks": CheckCircleIcon,
    "Done Tasks": CheckCircleIcon,
    "Active Users": GroupIcon,
    "Resigned Users": GroupIcon,
    // "Customers": AssignmentIndIcon,
    "Roles": MergeTypeIcon,
    "Audit Log": HistoryIcon,
    "Loan Types": ListIcon,
    "Mobile App Build": Settings,
    "Settings": Settings,
    "Pincodes": PersonPinCircle,
    "Loan Enquiries": ListIcon,
    "Product Master": WorkIcon,
    "Payments": PaymentIcon,
    "My Enquiries": ListIcon,
    "Caller List": AssignmentTurnedInIcon,
    "Caller Dashboard": DashboardIcon,
    "Caller Form": Assignment,
    "Daily Call Form": Assessment,
    "Prospects": TodayIcon,
    "Send Back Enquiries": AssignmentTurnedInIcon,
    "My QR": Assignment,
    "Onboard Merchant": Assignment,
    "QR Pool": ListIcon,
    "Agent Program": Assessment,
    "Agent Program Leads": ListIcon,
    "Agent Program Config": Settings,
    "Agent KYC Verification": AssignmentIndIcon,
    "Payout Requests": PaymentIcon,
    "Agent Wallet": AccountBalanceWalletIcon,
    "Overview": DashboardIcon,
  }

  // ===== HELPERS =====
  const findGroupForPath = (path) => {
    for (const group of menuGroups) {
      if (group.items) {
        for (const item of group.items) {
          if (item.to === path) return group.id
        }
      }
      if (group.subGroups) {
        for (const subGroup of group.subGroups) {
          for (const item of subGroup.items) {
            if (item.to === path) return group.id
          }
        }
      }
    }
    return 'dashboard'
  }

  const findSubGroupForPath = (path) => {
    for (const group of menuGroups) {
      if (group.subGroups) {
        for (const subGroup of group.subGroups) {
          for (const item of subGroup.items) {
            if (item.to === path) return subGroup.id
          }
        }
      }
    }
    return ''
  }

  // ===== EFFECTS =====
  useEffect(() => {
    const refreshIdentity = () => dispatch(refreshCurrentUser())
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshIdentity()
    }

    refreshIdentity()
    window.addEventListener('focus', refreshIdentity)
    document.addEventListener('visibilitychange', refreshWhenVisible)

    return () => {
      window.removeEventListener('focus', refreshIdentity)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [dispatch])

  useEffect(() => {
    if (userInfo) {
      setName(userInfo.name)
      if (userInfo.role && typeof userInfo.role === 'object') setRole(userInfo.role)
      if (userInfo.role?._id) dispatch(getRole())
    }
  }, [userInfo, dispatch])

  // Re-fetch role on every page navigation so permission changes take effect immediately
  useEffect(() => {
    if (userInfo) {
      dispatch(getRole())
    }
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (getRoleData) {
      setRole(getRoleData)
    }
  }, [getRoleData, errorGetRole])

  useEffect(() => {
    const currentGroup = findGroupForPath(location.pathname)
    const currentSubGroup = findSubGroupForPath(location.pathname)
    setExpandedGroup(currentGroup)
    if (currentSubGroup) setExpandedSubGroup(currentSubGroup)
  }, [location.pathname])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  // ===== ACTIONS =====
  const handleLogout = () => {
    dispatch(logout())
    navigate('/')
    setIsMobileMenuOpen(false)
  }

  const toggleGroup = (groupName) => {
    if (expandedGroup === groupName) {
      setExpandedGroup('')
      setExpandedSubGroup('')
    } else {
      setExpandedGroup(groupName)
      setExpandedSubGroup('')
    }
  }

  const toggleSubGroup = (subGroupName) => {
    if (expandedSubGroup === subGroupName) setExpandedSubGroup('')
    else setExpandedSubGroup(subGroupName)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  // ===== LINK =====
  const NavLink = ({ to, label, visible }) => {
    if (!visible) return null
    const isActive = location.pathname === to
    const Icon = iconMapping[label] || Assignment

    return (
      <Link
        to={to}
        className={`flex items-center px-4 py-3 text-sm sm:text-base transition-all duration-200 no-underline hover:no-underline ${
          isActive
            ? 'text-[rgb(254,240,138)] bg-[rgba(254,240,138,0.1)]'
            : 'text-white hover:text-[rgb(204,188,72)] hover:bg-[rgba(254,240,138,0.05)]'
        }`}
      >
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
        <span className="ml-3 font-medium">{label}</span>
      </Link>
    )
  }

  // ===== SIDEBAR CONTENT =====
  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-[rgba(255,255,255,0.1)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/brand-logo.png" alt="" className="h-8 sm:h-9 w-auto" />
            <h3 className="text-xl sm:text-2xl font-semibold text-white">Loan Enquiry</h3>
          </div>
          {/* Close button for mobile */}
          <button
            onClick={toggleMobileMenu}
            className="sm:hidden text-white hover:text-[rgb(204,188,72)] transition-colors"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 px-2 sm:px-3 py-4 space-y-1 overflow-y-auto overscroll-contain">
        {!loadingGetRole && menuGroups.map((group) => {
          const hasVisibleItems = group.subGroups
            ? group.subGroups.some(subGroup => subGroup.items.some(item => item.visible))
            : group.items?.some(item => item.visible)

          if (!hasVisibleItems) return null

          return (
            <div key={group.id} className="mb-2">
              <button
                onClick={() => toggleGroup(group.id)}
                className="flex items-center justify-between w-full px-3 sm:px-4 py-2 text-base sm:text-lg font-semibold text-white hover:bg-[rgba(254,240,138,0.1)] hover:text-[rgb(204,188,72)] rounded transition-all duration-200"
              >
                <span className="font-bold">{group.title}</span>
                {expandedGroup === group.id ? 
                  <ExpandLess className="w-5 h-5 sm:w-6 sm:h-6" /> : 
                  <ExpandMore className="w-5 h-5 sm:w-6 sm:h-6" />
                }
              </button>

              {expandedGroup === group.id && (
                <div className="pl-1 sm:pl-2 mt-1">
                  {group.subGroups ? (
                    group.subGroups.map((subGroup) => {
                      const hasVisibleSubItems = subGroup.items.some(item => item.visible)
                      if (!hasVisibleSubItems) return null

                      return (
                        <div key={subGroup.id} className="mb-1">
                          <button
                            onClick={() => toggleSubGroup(subGroup.id)}
                            className="flex items-center justify-between w-full px-3 py-2 text-sm sm:text-base font-medium text-gray-200 hover:bg-[rgba(254,240,138,0.1)] hover:text-[rgb(204,188,72)] rounded transition-all duration-200"
                          >
                            <span>{subGroup.title}</span>
                            {expandedSubGroup === subGroup.id ? 
                              <ExpandLess className="w-4 h-4 sm:w-5 sm:h-5" /> : 
                              <ExpandMore className="w-4 h-4 sm:w-5 sm:h-5" />
                            }
                          </button>
                          {expandedSubGroup === subGroup.id && (
                            <div className="pl-3 sm:pl-4 mt-1">
                              {subGroup.items.map((item) => (
                                <NavLink key={item.to} to={item.to} label={item.label} visible={item.visible} />
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })
                  ) : (
                    group.items?.map((item) => (
                      <NavLink key={item.to} to={item.to} label={item.label} visible={item.visible} />
                    ))
                  )}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* User Profile */}
      <div className="flex-shrink-0 border-t border-[rgba(255,255,255,0.1)] pb-[env(safe-area-inset-bottom)]">
        <button
          onClick={handleLogout}
          className="flex items-center w-full px-4 sm:px-6 py-3 sm:py-4 space-x-3 text-white hover:bg-[rgba(254,240,138,0.1)] hover:text-[rgb(204,188,72)] transition-colors duration-200"
        >
          <div className="flex-shrink-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[rgba(254,240,138,0.2)] flex items-center justify-center">
              <span className="text-[rgb(254,240,138)] font-medium text-sm sm:text-base">
                {(name?.[0] || '?').toUpperCase()}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs sm:text-sm font-medium text-white truncate">{name || 'User'}</div>
            <div className="text-[10px] sm:text-xs text-gray-300 truncate">Role: {role?.name || '—'}</div>
          </div>
          <PowerSettingsNewIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </button>
      </div>
    </>
  )

  // ===== RENDER =====
  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        onClick={toggleMobileMenu}
        className="fixed top-4 left-4 z-50 sm:hidden bg-[#013975] text-white p-2 rounded-lg shadow-lg hover:bg-[#014a8f] transition-colors"
        aria-label="Toggle menu"
      >
        <MenuIcon className="w-6 h-6" />
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 left-0
          w-4/5 xs:w-3/4 sm:w-1/5
          h-[100dvh] max-h-[100dvh] sm:h-screen sm:max-h-screen
          bg-[#013975]
          shadow-lg
          flex flex-col justify-between
          overflow-hidden
          z-50
          transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
        `}
      >
        <SidebarContent />
      </div>
    </>
  )
}

export default Sidebar
